import React, { useMemo } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { PromptExecutionConfig } from '../services/api';
import {
  normalizePromptExecutionConfig,
} from '../features/workflows/config';
import CustomSelect from './CustomSelect';
import './PromptExecutionConfigEditor.css';

interface PromptExecutionConfigEditorProps {
  value: PromptExecutionConfig;
  onChange: (value: PromptExecutionConfig) => void;
  editable?: boolean;
}

const SEGMENTATION_OPTIONS = [
  { value: 'content_aware', label: 'Content-aware', description: 'Adapt segment boundaries to changes in visual content, audio, and topics.' },
  { value: 'fixed', label: 'Fixed duration', description: 'Use equal-length clips. Useful for regular observation windows and consistent sampling.' },
];

const PromptExecutionConfigEditor: React.FC<PromptExecutionConfigEditorProps> = ({
  value,
  onChange,
  editable = true,
}) => {
  const config = useMemo(() => normalizePromptExecutionConfig(value), [value]);
  const patch = (updates: Partial<PromptExecutionConfig>) => {
    onChange(normalizePromptExecutionConfig({ ...config, ...updates }));
  };
  const mode = config.video_segmentation_type;
  const duration = config.video_segment_duration;
  const setSegmentation = (nextMode: string) => {
    patch({
      video_segmentation_type: nextMode as PromptExecutionConfig['video_segmentation_type'],
      video_segment_duration: nextMode === 'fixed' ? config.video_segment_duration || 10 : null,
    });
  };

  return (
    <div className="prompt-execution-config">
      <div className="prompt-execution-config-heading">
        <SlidersHorizontal size={16} aria-hidden="true" />
        <div>
          <h4>Prepare your media</h4>
          <p>Choose how media is prepared for extraction.</p>
        </div>
      </div>

      <div className="prompt-execution-config-grid">
        <div className="prompt-execution-field">
          <span>Segmentation</span>
          {editable ? (
            <CustomSelect
              ariaLabel="Video segmentation"
              value={mode}
              options={SEGMENTATION_OPTIONS}
              onChange={setSegmentation}
            />
          ) : (
            <span className="prompt-execution-value-chip">{SEGMENTATION_OPTIONS.find((option) => option.value === mode)?.label}</span>
          )}
        </div>
        {mode === 'fixed' && (
          <label className="prompt-execution-field">
            <span>Segment duration</span>
            {editable ? (
              <div className="prompt-execution-duration">
                <input
                  aria-label="Video segment duration"
                  type="number"
                  min={1}
                  max={300}
                  value={duration ?? ''}
                  onChange={(event) => patch({ video_segment_duration: Number(event.target.value) })}
                />
                <span>seconds</span>
              </div>
            ) : (
              <span className="prompt-execution-value-chip">{duration} seconds</span>
            )}
          </label>
        )}
      </div>
      <p className="prompt-execution-config-help">
        {SEGMENTATION_OPTIONS.find((option) => option.value === mode)?.description}
      </p>

      <div className="prompt-execution-toggles">
        <label className="prompt-execution-option">
          {editable && (
            <input
              type="checkbox"
              checked={config.enable_image_embedding}
              onChange={(event) => patch({ enable_image_embedding: event.target.checked })}
            />
          )}
          <span className="prompt-execution-option-copy">
            <span className="prompt-execution-option-title">Visual search</span>
            <span className="prompt-execution-config-help">Create image embeddings so extracted media can be found by visual similarity.</span>
          </span>
          <span className={'prompt-execution-value-chip' + (config.enable_image_embedding ? ' enabled' : '')}>{config.enable_image_embedding ? 'On' : 'Off'}</span>
        </label>
      </div>
    </div>
  );
};

export default PromptExecutionConfigEditor;
